import { FC, useEffect, useRef } from "react";
import { PlottingData } from "refsix-js-models";
import HeatMapRenderer, { DataPoint } from "heatmap-ts";
import { HeatmapConstants } from "../../../constants/HeatmapConstants";

interface ReplayHeatmapProps {
    plottingData: PlottingData;
    // Draw the heatmap progressively up to the current GPS index.
    currentIndex: number;
}

/**
 * Heatmap layer that builds up as the video plays, drawing only the points
 * visited up to the current playback position. Rendered as an absolutely
 * positioned layer over the pitch image. Ported from the legacy
 * LiveHeatmapDirective.
 */
const ReplayHeatmap: FC<ReplayHeatmapProps> = ({
    plottingData,
    currentIndex,
}) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const rendererRef = useRef<HeatMapRenderer | null>(null);

    const width = plottingData.width;
    const height = Math.ceil(plottingData.height);

    // (Re)create the renderer whenever the canvas size changes. Setting the
    // canvas width/height imperatively is required — leaving it to React props
    // alone can leave the drawing buffer at its default size and nothing paints.
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !width || !height) {
            return;
        }
        canvas.width = width;
        canvas.height = height;
        rendererRef.current = new HeatMapRenderer({ canvas, width, height });
    }, [width, height]);

    // Repaint the cumulative subset of points each time playback advances.
    useEffect(() => {
        const renderer = rendererRef.current;
        if (!renderer || !plottingData.data) {
            return;
        }
        const pixelsPerMeter = plottingData.pixelsPerMeter || 1;
        const points: DataPoint[] = plottingData.data
            .slice(0, currentIndex)
            .map((point) => ({
                ...point,
                value: HeatmapConstants.POINT_VALUE,
                radius: HeatmapConstants.POINT_RADIUS_METERS * pixelsPerMeter,
            }));
        renderer.setData({
            data: points,
            max: plottingData.data.length * HeatmapConstants.HEATMAP_INTENSITY,
        });
        renderer.repaint();
    }, [plottingData, currentIndex]);

    return <canvas ref={canvasRef} className="replay-layer replay-heatmap-canvas" />;
};

export default ReplayHeatmap;
